import React, {useState} from 'react';

import {Button, Icon, Input, Label, Text} from '@albert-io/atomic';

import {stateMap} from 'client/AccountSettings/ContentPersonalization/ContentPersonalization.util';

import FormDropdown from './FormDropdown';
import {COUNTRY_OPTIONS, USA} from './schoolAssociation.utils';

import type {CreatedHomeschool, DropdownOption} from './schoolAssociation.types';

interface CreateHomeschoolFormProps {
  onSwitchToSearch: () => void;
  onCreated: (school: CreatedHomeschool) => void;
  initialData?: CreatedHomeschool | null;
  /** Used on fresh form (no initialData) to pre-fill school name; editable. */
  defaultName?: string;
}

export default function CreateHomeschoolForm({
  onSwitchToSearch,
  onCreated,
  initialData,
  defaultName = ''
}: CreateHomeschoolFormProps) {
  const [name, setName] = useState(initialData?.name ?? defaultName);
  const [country, setCountry] = useState(initialData?.country ?? USA);
  const [city, setCity] = useState(initialData?.city ?? '');
  const [state, setState] = useState(initialData?.state ?? '');

  const isUsa = country === USA;
  // name/city/country always required. state required only for USA.
  const isValid = Boolean(name.trim() && city.trim() && country && (!isUsa || state));

  const handleCountryChange = (next: string) => {
    setCountry(next);
    if (next !== USA) setState('');
  };

  const stateOptions: DropdownOption[] = Object.entries(stateMap).map(([abbr, stateName]) => ({
    label: `${stateName} (${abbr})`,
    value: abbr
  }));

  return (
    <div className='flex flex-col gap-x5'>
      <div className='flex flex-col gap-x1-half'>
        <div>
          <Label>School Name</Label>
          <Input
            className='mt-x1'
            type='text'
            placeholder="e.g. Albert's High School"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />
        </div>
        <FormDropdown
          label='Country'
          value={country}
          placeholder='Select a Country'
          options={COUNTRY_OPTIONS}
          onChange={handleCountryChange}
          testId='homeschool-country-dropdown'
        />
        <div className='flex gap-x1-half'>
          <div className='flex-1'>
            <Label>City</Label>
            <Input
              className='mt-x1'
              type='text'
              placeholder='e.g. Chicago'
              value={city}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
            />
          </div>
          <div className='flex-1'>
            <FormDropdown
              label='State'
              value={state}
              placeholder='Select a State'
              options={stateOptions}
              onChange={setState}
              disabled={!isUsa}
              testId='homeschool-state-dropdown'
            />
          </div>
        </div>
      </div>
      <div className='flex gap-x1-half'>
        <Button
          color='secondary'
          variant='faded'
          roundness='pill'
          className='flex-1 gap-x1-quarter'
          onClick={onSwitchToSearch}
        >
          <Icon icon='arrow-left' />
          <Text color='secondary'>Search Instead</Text>
        </Button>
        <Button
          color='primary'
          roundness='pill'
          className='flex-1 gap-x1-quarter'
          disabled={!isValid}
          onClick={() => onCreated({name: name.trim(), country, city: city.trim(), state})}
        >
          <Text color='primary-inverse-static'>Create Homeschool</Text>
        </Button>
      </div>
    </div>
  );
}
